import { Link, createFileRoute } from "@tanstack/react-router";
import { Panel } from "@/components/primitives";
import { StageTag } from "@/components/status";
import { useYard } from "@/lib/use-yard";
import { fmtClock, fmtDate } from "@/lib/yard-data";
import { STATUS_TEXT } from "./disputes.index";

export const Route = createFileRoute("/disputes/$disputeId")({
  head: () => ({
    meta: [
      { title: "Dispute record — PortDwellr" },
      { name: "description", content: "The full timestamped trail for a disputed container: gate in, assignment, unload, gate out." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DisputeDetail,
});

function DisputeDetail() {
  const { disputeId } = Route.useParams();
  const { disputes } = useYard();
  const dispute = disputes.find((d) => d.id === disputeId);

  if (!dispute) {
    return (
      <div>
        <h1 className="font-display text-[26px] font-semibold">Dispute not found</h1>
        <Link to="/disputes" className="mt-4 inline-block font-mono text-[12px] uppercase tracking-[0.1em] text-steel underline">
          Back to disputes
        </Link>
      </div>
    );
  }

  const first = dispute.timeline[0]!;
  const last = dispute.timeline[dispute.timeline.length - 1]!;
  const totalMins = Math.round((last.at - first.at) / 60000);

  return (
    <>
      <Link to="/disputes" className="font-mono text-[11px] uppercase tracking-[0.1em] text-steel-mid hover:text-steel">
        ← All disputes
      </Link>

      <div className="mt-6 border-b border-line pb-7">
        <span className="eyebrow">RESOLVE · here's the record</span>
        <h1 className="mt-4 font-display text-[30px] font-semibold leading-[1.1] md:text-[36px]">
          {dispute.container}
        </h1>
        <p className="mt-3 max-w-[64ch] text-[15px] text-ink-soft">{dispute.summary}</p>
      </div>

      <div className="mt-8 grid grid-cols-1 border-l border-t border-line sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Dispute", `${dispute.id} · ${dispute.type}`],
          ["Status", STATUS_TEXT[dispute.status]],
          ["Carrier", dispute.carrier],
          ["Claimed", dispute.amount > 0 ? `$${dispute.amount.toLocaleString()}` : "—"],
        ].map(([k, v]) => (
          <div key={k} className="border-b border-r border-line bg-paper-raised p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-steel-mid">{k}</div>
            <div className="mt-2 font-mono text-[15px] text-steel">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <Panel title={`Timestamp trail · ${totalMins} min door-to-door`}>
          <ol className="px-5 py-2 md:px-6">
            {dispute.timeline.map((e, i) => (
              <li key={i} className="grid grid-cols-1 gap-2 border-b border-line py-5 last:border-b-0 xs:grid-cols-[86px_1fr] xs:gap-4">
                <div className="flex gap-2 font-mono text-[13px] text-steel xs:block">
                  <div>{fmtClock(e.at)}</div>
                  <div className="text-[11px] text-steel-mid">{fmtDate(e.at)}</div>
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <StageTag stage={e.stage} />
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-steel-mid">
                      source: {e.source}
                    </span>
                  </div>
                  <p className="mt-2 text-[15px] text-ink">{e.label}</p>
                </div>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      <p className="mt-6 max-w-[70ch] font-mono text-[11px] uppercase tracking-[0.08em] text-steel-mid">
        This trail is machine-recorded at each touchpoint. Export it and the argument is over.
      </p>
    </>
  );
}
